import { useState, useCallback } from 'react';
import { apiFetch } from '../lib/api';
import type { StrategicDirection } from '../lib/strategicDirections';

export interface PersistedDirections {
  id: string;
  package_id: string;
  directions: StrategicDirection[];
  chosen_index: number | null;
  created_at: string;
}

export interface PersistedMaterials {
  id: string;
  package_id: string;
  direction_id: string;
  materials: { content: string; adImages?: Record<string, string> };
  selections: Record<string, unknown> | null;
  created_at: string;
  updated_at: string;
}

export function useMarketingPersistence(packageId: string | null) {
  const [directionsData, setDirectionsData] = useState<PersistedDirections | null>(null);
  const [materialsData, setMaterialsData] = useState<PersistedMaterials | null>(null);
  const [loading, setLoading] = useState(false);

  const loadDirections = useCallback(async () => {
    if (!packageId) return;
    setLoading(true);
    try {
      const res = await apiFetch(`/api/packages/${packageId}/directions`);
      if (res.ok) {
        const data = await res.json();
        setDirectionsData(data);
      }
    } catch { /* DB not available */ }
    setLoading(false);
  }, [packageId]);

  const saveDirections = useCallback(async (directions: StrategicDirection[], chosenIndex?: number) => {
    if (!packageId) return null;
    try {
      const res = await apiFetch(`/api/packages/${packageId}/directions`, {
        method: 'POST',
        body: JSON.stringify({ directions, chosenIndex: chosenIndex ?? null }),
      });
      if (res.ok) {
        const data = await res.json();
        setDirectionsData(data);
        return data as PersistedDirections;
      }
    } catch { /* ignore */ }
    return null;
  }, [packageId]);

  const updateChosenDirection = useCallback(async (dirId: string, chosenIndex: number) => {
    if (!packageId) return;
    try {
      const res = await apiFetch(`/api/packages/${packageId}/directions/${dirId}`, {
        method: 'PUT',
        body: JSON.stringify({ chosenIndex }),
      });
      if (res.ok) {
        const data = await res.json();
        setDirectionsData(data);
      }
    } catch { /* ignore */ }
  }, [packageId]);

  const loadMaterials = useCallback(async () => {
    if (!packageId) return;
    try {
      const res = await apiFetch(`/api/packages/${packageId}/materials`);
      if (res.ok) {
        const data = await res.json();
        setMaterialsData(data);
        return data as PersistedMaterials | null;
      }
    } catch { /* ignore */ }
    return null;
  }, [packageId]);

  const saveMaterials = useCallback(async (directionId: string, content: string, adImages?: Record<string, unknown>) => {
    if (!packageId) return null;
    try {
      const res = await apiFetch(`/api/packages/${packageId}/materials`, {
        method: 'POST',
        body: JSON.stringify({
          directionId,
          materials: { content, adImages: adImages ?? {} },
          selections: null,
        }),
      });
      if (res.ok) {
        const data = await res.json();
        setMaterialsData(data);
        return data as PersistedMaterials;
      }
    } catch { /* ignore */ }
    return null;
  }, [packageId]);

  const updateMaterials = useCallback(async (matId: string, adImages: Record<string, unknown>) => {
    if (!packageId) return;
    try {
      const res = await apiFetch(`/api/packages/${packageId}/materials/${matId}`, {
        method: 'PUT',
        body: JSON.stringify({ selections: { adImages } }),
      });
      if (res.ok) {
        const data = await res.json();
        setMaterialsData(data);
      }
    } catch { /* ignore */ }
  }, [packageId]);

  return {
    directionsData, materialsData, loading,
    loadDirections, saveDirections, updateChosenDirection,
    loadMaterials, saveMaterials, updateMaterials,
  };
}
